import { FunctionComponent, useEffect } from 'react'
import { Grid, SelectChangeEvent, Typography } from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'
import { DialogWrapper } from '../../display-elements-components/dialog-wrapper'
import { FormSelectInputDisplay } from '../../form-field-components/new-and-edit/form-select-input'
import { FormTextInputDisplay } from '../../form-field-components/new-and-edit/form-text-input'
import { FormDateInputDisplay } from '../../form-field-components/new-and-edit/form-date-input'
import {
  CertificationHolderModelName,
  CertificationStandardKey,
  ICertificationCatalog,
  IManagementSystemCertification,
  IPostManagementSystemCertification,
} from '../../../../shared/services/management-system-certification/management-system-certification.model'

const formatIndicativeDate = (isoDate: string): string => moment(isoDate, 'YYYY-MM-DD').format('D MMMM YYYY')

function toFormValues(existing: IManagementSystemCertification | null, catalog: ICertificationCatalog | null): IPostManagementSystemCertification {
  if (existing) {
    return {
      standard: existing.standard,
      edition: existing.edition,
      certificateNumber: existing.certificateNumber || '',
      certificationBody: existing.certificationBody || '',
      scopeDescription: existing.scopeDescription || '',
      issuedOn: existing.issuedOn || '',
      expiresOn: existing.expiresOn || '',
      nextAuditOn: existing.nextAuditOn || '',
    }
  }
  const first = catalog?.standards[0]
  return {
    standard: first ? first.key : '',
    edition: first ? first.currentEdition : '',
    certificateNumber: '',
    certificationBody: '',
    scopeDescription: '',
    issuedOn: '',
    expiresOn: '',
    nextAuditOn: '',
  }
}

/**
 * Add or edit one management-system certificate. The dialog owns the form; the panel owns the
 * save (create or update) and passes `isSubmitting` back in.
 */
export const AddEditCertificationDialog: FunctionComponent<{
  open: boolean,
  onClose: () => void,
  onSubmit: (values: IPostManagementSystemCertification) => void,
  isSubmitting: boolean,
  catalog: ICertificationCatalog | null,
  holderModelName: CertificationHolderModelName,
  existing: IManagementSystemCertification | null,
}> = ({ open, onClose, onSubmit, isSubmitting, catalog, holderModelName, existing }) => {

  const schema = Yup.object().shape({
    standard: Yup.string().required('Choose a standard'),
    edition: Yup.string().required('Choose an edition'),
    certificateNumber: Yup.string().nullable().max(100, 'Keep the certificate number under 100 characters'),
    certificationBody: Yup.string().nullable().max(200, 'Keep the certification body under 200 characters'),
    scopeDescription: Yup.string().nullable().max(2000, 'Keep the scope under 2,000 characters'),
    issuedOn: Yup.string().nullable(),
    expiresOn: Yup.string().nullable().test('after-issue', 'The expiry date must be on or after the issue date', function (value) {
      const issued = this.parent.issuedOn
      if (!value || !issued) return true
      return value >= issued
    }),
    nextAuditOn: Yup.string().nullable(),
  })

  const formik = useFormik<IPostManagementSystemCertification>({
    validationSchema: schema,
    enableReinitialize: false,
    initialValues: toFormValues(existing, catalog),
    onSubmit: (values) => onSubmit(values),
  })

  // Reset on every open so an edit never shows the previous row's values, and a fresh add starts
  // blank on the catalogue's first standard.
  useEffect(() => {
    if (open) formik.resetForm({ values: toFormValues(existing, catalog) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing, catalog])

  const standards = catalog?.standards || []
  const selectedStandard = standards.find((s) => s.key === formik.values.standard) || null
  const editionOptions = (selectedStandard?.editions || []).map((e) => ({
    value: e.edition,
    label: e.supersededBy ? `${e.label} (superseded)` : e.label,
  }))
  const selectedEdition = selectedStandard?.editions.find((e) => e.edition === formik.values.edition) || null

  const standardChanged = (event: SelectChangeEvent) => {
    const key = event.target.value as CertificationStandardKey
    const standard = standards.find((s) => s.key === key)
    formik.setValues({ ...formik.values, standard: key, edition: standard ? standard.currentEdition : '' })
  }

  const dateValue = (value: string | null | undefined) => (value ? moment(value, 'YYYY-MM-DD') : null)
  const dateChanged = (field: keyof IPostManagementSystemCertification) => (value: any) =>
    formik.setFieldValue(field, value && moment(value).isValid() ? moment(value).format('YYYY-MM-DD') : '')

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title={existing ? 'Edit certificate' : 'Add certificate'}
      cancelHandler={onClose}
      submitHandler={formik.submitForm}
      isSubmitting={isSubmitting}
      disableSaveButton={isSubmitting || !catalog}
      maxWidth='md'
    >
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6}>
          <FormSelectInputDisplay
            isEditPage={false}
            disabled={isSubmitting}
            title='Standard'
            name='standard'
            value={formik.values.standard}
            touched={formik.touched.standard}
            errors={formik.errors.standard}
            onChange={standardChanged}
            array={standards.map((s) => ({ value: s.key, label: s.label }))}
            valueKey='value'
            labelKey='label'
            forceOutlined
            isTopRow
            isBottomRow
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormSelectInputDisplay
            isEditPage={false}
            disabled={isSubmitting || !selectedStandard}
            title='Edition'
            name='edition'
            value={formik.values.edition}
            touched={formik.touched.edition}
            errors={formik.errors.edition}
            onChange={(event: SelectChangeEvent) => formik.setFieldValue('edition', event.target.value)}
            array={editionOptions}
            valueKey='value'
            labelKey='label'
            forceOutlined
            isTopRow
            isBottomRow
          />
        </Grid>
        {selectedEdition?.supersededBy && selectedEdition.transitionDeadline && (
          <Grid item xs={12}>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Certificates to this edition cease to be valid on {formatIndicativeDate(selectedEdition.transitionDeadline)}, whatever
              their printed expiry, and new ones stop being issued after {selectedEdition.lastNewCertificateDate ? formatIndicativeDate(selectedEdition.lastNewCertificateDate) : 'the transition cut-off'}.
              These dates are indicative: confirm them with your certification body.
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <FormTextInputDisplay
            isEditPage={false}
            disabled={isSubmitting}
            title='Certificate number'
            name='certificateNumber'
            value={formik.values.certificateNumber || ''}
            touched={formik.touched.certificateNumber}
            errors={formik.errors.certificateNumber}
            onChange={formik.handleChange}
            forceOutlined
            regularFieldLabel
            isTopRow
            isBottomRow
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormTextInputDisplay
            isEditPage={false}
            disabled={isSubmitting}
            title='Certification body'
            name='certificationBody'
            value={formik.values.certificationBody || ''}
            touched={formik.touched.certificationBody}
            errors={formik.errors.certificationBody}
            onChange={formik.handleChange}
            forceOutlined
            regularFieldLabel
            isTopRow
            isBottomRow
          />
        </Grid>
        <Grid item xs={12}>
          <FormTextInputDisplay
            isEditPage={false}
            disabled={isSubmitting}
            title='Scope (sites and activities covered)'
            name='scopeDescription'
            value={formik.values.scopeDescription || ''}
            touched={formik.touched.scopeDescription}
            errors={formik.errors.scopeDescription}
            onChange={formik.handleChange}
            forceOutlined
            regularFieldLabel
            multiline
            rows={2}
            isTopRow
            isBottomRow
          />
        </Grid>
        <Grid item xs={12} md={holderModelName === 'organisation' ? 4 : 6}>
          <FormDateInputDisplay
            isEditPage={false}
            disabled={isSubmitting}
            title='Issued'
            name='issuedOn'
            value={dateValue(formik.values.issuedOn)}
            touched={formik.touched.issuedOn}
            errors={formik.errors.issuedOn}
            onChange={dateChanged('issuedOn')}
            isTopRow
            isBottomRow
          />
        </Grid>
        <Grid item xs={12} md={holderModelName === 'organisation' ? 4 : 6}>
          <FormDateInputDisplay
            isEditPage={false}
            disabled={isSubmitting}
            title='Expires'
            name='expiresOn'
            value={dateValue(formik.values.expiresOn)}
            touched={formik.touched.expiresOn}
            errors={formik.errors.expiresOn}
            onChange={dateChanged('expiresOn')}
            isTopRow
            isBottomRow
          />
        </Grid>
        {holderModelName === 'organisation' && (
          <Grid item xs={12} md={4}>
            <FormDateInputDisplay
              isEditPage={false}
              disabled={isSubmitting}
              title='Next audit'
              name='nextAuditOn'
              value={dateValue(formik.values.nextAuditOn)}
              touched={formik.touched.nextAuditOn}
              errors={formik.errors.nextAuditOn}
              onChange={dateChanged('nextAuditOn')}
              isTopRow
              isBottomRow
            />
          </Grid>
        )}
        {formik.touched.expiresOn && formik.errors.expiresOn && (
          <Grid item xs={12}>
            <Typography variant='body2' color='error'>{String(formik.errors.expiresOn)}</Typography>
          </Grid>
        )}
        {!existing && (
          <Grid item xs={12}>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              You can attach the certificate document from the row menu once it is saved.
            </Typography>
          </Grid>
        )}
      </Grid>
    </DialogWrapper>
  )
}
